import {AbstractControl, UntypedFormGroup} from '@angular/forms';
import {BehaviorSubject, Subject} from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {DOMService, ScreenService} from '../../../../shared/services/general';
import {DxDataGridComponent, DxDropDownBoxComponent} from 'devextreme-angular';

import {ClientService} from '../../services';
import DataSource from 'devextreme/data/data_source';
import {FusionAuthService} from '../../../../shared/services/fusion-auth';
import {User} from '../../../../shared/models/fusion-auth';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-client-selector',
  templateUrl: './client-selector.component.html',
  styleUrls: ['./client-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientSelectorComponent implements OnInit, OnDestroy {
  @ViewChild(DxDropDownBoxComponent, {static: true}) public clientDropDown?: DxDropDownBoxComponent;
  @ViewChild('grid', {static: false}) public grid?: DxDataGridComponent;
  @Input() public control: AbstractControl | null | undefined = new UntypedFormGroup({});
  @Input() public required ? = false;
  @Input() public validationGroup: string = '';
  @Input() public selection: 'single' | 'multiple' = 'single';
  @Input() public filterClients = new EventEmitter<number[]>();
  @Input() public orderId ?: number;
  @Input() public startDate ?: any;
  @Input() public endDate ?: any;
  @Input() public limit ?: number;
  @Input() public resultEntry?: boolean = false;
  @Input() public clearBtn = true;
  // @ts-ignore
  public dataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  public selectedRowKeys: number[] = [];
  public user: User;
  public role?: string;
  public editorElement: any;
  public isPhoneOrTablet: boolean = false;
  public isLoading: boolean = false;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private clientService: ClientService,
    private fusionAuthService: FusionAuthService,
    private renderer: Renderer2,
    private screen: ScreenService,
    private domService: DOMService,
    private ref: ChangeDetectorRef,
  ) {
    this.user = fusionAuthService.Profile;
    this.populateClients = this.populateClients.bind(this);
  }

  private waitByUser() {
    this.user = this.fusionAuthService.Profile;

    if (this.user) {
      this.populateClients();
    } else {
      setTimeout(() => {
        this.waitByUser();
      }, 1000);
    }
  }

  public ngOnInit(): void {
    this.control?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => {
        if (value === undefined || value === null) {
          this.clientDropDown?.instance.reset();
          this.selectedRowKeys = [];
        } else {
          if (this.selectedRowKeys.length === 0) {
            this.selectedRowKeys.push(value.id);
            this.clientDropDown?.instance.option('value', this.selectedRowKeys);
          }
        }
        if ((value === undefined || value == null || value.length === 0) && this.resultEntry) {
          this.grid?.instance?.clearSelection();
        }
        this.ref.detectChanges();
      });

    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    });

    this.filterClients.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.dataSource.next(new DataSource({
        store: this.clientService.getByRange(this.orderId as number, this.startDate, this.endDate, this.limit as number,
          this.role === 'Physician' ? this.user.data.physicians : [], this.dataSource)
      }));
    });

    this.waitByUser();
  }

  private populateClients(clientsIds: number[] = []) {
    this.isLoading = true;
    clientsIds = this.user.data ? this.user.data.clients : [];
    if (clientsIds && clientsIds.length > 0) {
      this.dataSource.next(new DataSource({store: this.clientService.customStoreFilteredByClientIds(clientsIds, this.dataSource)}));
    } else {
      this.role = this.user.registrations[0].roles[0];
      if (localStorage.getItem('authtoken')) {
        switch (this.role) {
          case 'Client Admin':
          case 'Client Basic':
            this.dataSource.next(new DataSource({store: this.clientService.customStoreFilteredByClientIds(clientsIds, this.dataSource)}));
            break;
          case 'Physician':
            this.dataSource.next(new DataSource({store: this.clientService.customStoreFilteredByPhysicianIds(this.user.data.physicians, this.dataSource)}));
            break;
          case 'Patient':
            this.dataSource.next(new DataSource({store: this.clientService.customStoreFilteredByPatientIds(this.user.data.patients, this.dataSource)}));
            break;
          default:
            this.dataSource.next(new DataSource({store: this.clientService.store(this.dataSource)}));
            break;
        }
        this.ref.detectChanges();
      } else {
        setTimeout(() => {
          this.populateClients();
        }, 1000);
      }
    }
  }

  public valueChanged(event: any) {
    if (!event.value) {
      this.control?.setValue(null);
      if (this.grid !== undefined) {
        this.grid.instance.clearSelection();
      }
    } else {
      if (this.selection === 'single') {
        this.clientDropDown?.instance.close();
        const client = this.grid?.instance.getSelectedRowsData()[0];
        if (client) {
          this.control?.setValue(client);
        }
      } else {
        this.control?.setValue(this.grid?.instance.getSelectedRowsData());
      }
    }
  }

  public resizePickerDropDown() {
    const element = this.domService.getElementsByClassname('dx-overlay-wrapper dx-dropdowneditor-overlay dx-popup-wrapper').item(0) as HTMLElement;
    if (element) {
      if (this.isPhoneOrTablet) {
        this.renderer.setStyle(element, 'width', '300px');
        this.renderer.setStyle(element.firstChild, 'width', '300px');
        this.renderer.setStyle(element.firstChild?.firstChild, 'width', '300px');
      } else {
        this.renderer.setStyle(element, 'width', '580px');
        this.renderer.setStyle(element.firstChild, 'width', '580px');
        this.renderer.setStyle(element.firstChild?.firstChild, 'width', '580px');
      }
      this.renderer.setStyle(element, 'height', '50vh');
      this.renderer.setStyle(element.firstChild, 'height', '50vh');
      this.renderer.setStyle(element.firstChild?.firstChild, 'height', '50vh');

      if (this.grid !== undefined) {
        this.grid.instance.clearFilter();
        setTimeout(() => {
          this.editorElement.focus();
        }, 300);
      }
    }
  }

  public onCellPrepared(e: any) {
    if (e.rowType && e.columnIndex) {
      if (e.rowType === 'filter' && e.columnIndex === 1) {
        const cellElement = e.cellElement;
        this.editorElement = cellElement.querySelector('.dx-texteditor-input');
      }
    }
  }

  public ngOnDestroy(): void {
    this.dataSource.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
